/**
 * Security utilities for the application
 */

/**
 * Maps Firebase Identity Toolkit error codes to user-facing copy.
 */
const FIREBASE_AUTH_MESSAGES: Record<string, string> = {
  INVALID_OOB_CODE:
    'This reset link is invalid or has already been used. Request a new one from the login page.',
  EXPIRED_OOB_CODE:
    'This reset link has expired. Request a new one from the login page.',
  WEAK_PASSWORD:
    'Password is too weak. Use at least 8 characters with letters and numbers.',
  OPERATION_NOT_ALLOWED:
    'Password reset is not available for this account. Contact support.',
  USER_DISABLED: 'This account has been disabled. Contact support.',
};

const formatAuthErrorMessage = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  return FIREBASE_AUTH_MESSAGES[trimmed] ?? trimmed;
};

/**
 * Extract a human-readable message from MedoBill API / axios errors.
 * Our FastAPI handlers return `{ message: "..." }`, not `{ detail: "..." }`.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (data) {
    if (typeof data.message === 'string' && data.message.trim()) {
      return formatAuthErrorMessage(data.message);
    }
    if (typeof data.detail === 'string' && data.detail.trim()) {
      return formatAuthErrorMessage(data.detail);
    }
    if (Array.isArray(data.detail) && data.detail.length > 0) {
      const first = data.detail[0];
      if (typeof first === 'object' && first !== null && 'msg' in first) {
        return String((first as { msg: unknown }).msg);
      }
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
};

/**
 * Sanitizes error messages to prevent information leakage
 * Only shows generic messages in production, detailed in development
 */
export const sanitizeErrorMessage = (error: any, defaultMessage: string = 'An error occurred'): string => {
  if (process.env.NODE_ENV === 'development') {
    // In development, show detailed errors
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.response?.data?.detail) {
      return typeof error.response.data.detail === 'string' 
        ? error.response.data.detail 
        : JSON.stringify(error.response.data.detail);
    }
    if (error?.message) {
      return error.message;
    }
    return defaultMessage;
  }

  // In production, only show safe, generic messages
  if (error?.response?.status === 401) {
    return 'Authentication failed. Please log in again.';
  }
  if (error?.response?.status === 403) {
    return 'You do not have permission to perform this action.';
  }
  if (error?.response?.status === 404) {
    return 'The requested resource was not found.';
  }
  if (error?.response?.status === 422) {
    return error?.response?.data?.message || 'Invalid data provided. Please check your input.';
  }
  if (error?.response?.status === 500) {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string' && detail.length < 200) {
      return detail;
    }
    return 'A server error occurred. Please try again later.';
  }
  if (error?.response?.status >= 400 && error?.response?.status < 500) {
    return getApiErrorMessage(error, 'An error occurred. Please try again.');
  }
  if (error?.message?.includes('Network Error')) {
    return 'Network error. Please check your connection.';
  }
  if (error?.message?.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }
  return defaultMessage;
};

/**
 * Validates that API base URL uses HTTPS in production
 */
export const validateApiUrl = (url: string): boolean => {
  if (process.env.NODE_ENV === 'production') {
    // Allow relative URLs (they inherit the page protocol)
    if (url.startsWith('/')) {
      return true;
    }
    return url.startsWith('https://');
  }
  return true; // Allow HTTP in development
};

/**
 * Sanitizes user input to prevent XSS attacks
 * React escapes by default, but this provides an extra layer
 */
export const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== 'string') {
    return '';
  }
  // Remove potentially dangerous characters while keeping valid input
  return input
    .replace(/[<>]/g, '') // Remove angle brackets
    .trim();
};

/**
 * Sanitizes HTML content if needed (for future use)
 */
export const sanitizeHtml = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }
  // Basic HTML sanitization - React escapes by default, but this is extra protection
  const div = document.createElement('div');
  div.textContent = html;
  return div.innerHTML;
};

/**
 * Creates a safe logger that only logs in development
 */
export const safeLog = {
  log: (...args: any[]) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(...args);
    }
  },
  info: (...args: any[]) => {
    if (process.env.NODE_ENV === 'development') {
      console.info(...args);
    }
  },
  warn: (...args: any[]) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(...args);
    }
  },
  error: (...args: any[]) => {
    // Errors should always be logged, but sanitized in production
    if (process.env.NODE_ENV === 'development') {
      console.error(...args);
    } else {
      // In production, log to error tracking service (e.g., Sentry)
      // For now, just log a sanitized version
      const sanitized = args.map(arg => {
        if (arg instanceof Error) {
          return { message: arg.message, stack: '[REDACTED]' };
        }
        return arg;
      });
      console.error('[Production Error]', ...sanitized);
    }
  },
  debug: (...args: any[]) => {
    if (process.env.NODE_ENV === 'development') {
      console.debug(...args);
    }
  },
};

